import { RADIUS } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { pushChannelCopy } from "~/components/notifications/channel-copy";
import { track } from "~/lib/analytics";
import { isNative } from "~/lib/capacitor";
import {
  ensureNotificationPermission,
  notificationPermissionState,
} from "~/lib/push-notifications";
import { getItem, setItem, StorageKey } from "~/lib/storage";
import { useAuthStore } from "~/store/auth-store";
import { AnalyticsEventName } from "~/types/analytics";

/**
 * The one place the app asks for the system notification permission on its own.
 *
 * iOS grants exactly one dialog per install, so where it is spent matters more than
 * how often it is offered: a member reading a seven-day webinar reminder has just
 * demonstrated that they want to be told, which is the highest-intent moment the
 * product has. On the web every guard below resolves to "not applicable" and the
 * card never renders — a browser has no phone to notify.
 *
 * Asked and declined is a decision, not a state to nag about: the card removes
 * itself after either button, and "Teď ne" is remembered across launches.
 */
export function NotificationPermissionPrompt() {
  const { t } = useTranslation();
  const userId = useAuthStore((state) => state.user?.id) ?? "";
  const [visible, setVisible] = useState(false);
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      if (!isNative()) return;
      if (await getItem(StorageKey.NotificationPromptDismissed)) return;
      const state = await notificationPermissionState();
      // Only "prompt": a granted device needs no card, and a denied one cannot be
      // rescued from inside the app — that copy belongs in the settings screen.
      if (!cancelled && state === "prompt") setVisible(true);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!visible || !userId) return null;

  const enable = async () => {
    setAsking(true);
    try {
      await ensureNotificationPermission({
        userId,
        channels: pushChannelCopy(t),
      });
      if ((await notificationPermissionState()) === "granted") {
        track(AnalyticsEventName.PushPermissionGranted, {
          subjectType: "notification",
        });
      }
    } catch {
      // A permission the OS refused to ask about is not an error the member can act
      // on; the card goes away either way and the settings screen states the state.
    } finally {
      setVisible(false);
    }
  };

  const dismiss = () => {
    void setItem(StorageKey.NotificationPromptDismissed, "1");
    setVisible(false);
  };

  return (
    <Card
      variant="outlined"
      data-testid="notifications.prompt"
      sx={{
        borderColor: "primary.main",
        borderRadius: `${RADIUS.lg}px`,
        padding: { xs: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Typography variant="H3" sx={{ color: "text.primary" }}>
        {t("notifications.prompt.title")}
      </Typography>

      <Typography variant="T2" sx={{ color: "text.secondary" }}>
        {t("notifications.prompt.body")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          marginTop: 1,
        }}
      >
        <Button
          variant="contained"
          disabled={asking}
          onClick={() => void enable()}
          data-testid="notifications.prompt-enable"
        >
          {t("notifications.prompt.enable")}
        </Button>

        <Button
          variant="text"
          onClick={dismiss}
          data-testid="notifications.prompt-dismiss"
        >
          {t("notifications.prompt.dismiss")}
        </Button>
      </Box>
    </Card>
  );
}
